import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import { X, Trophy, AlertTriangle } from 'lucide-react';
import { Tournament, Team, Player, RankingConfig } from '../../types';
import { doc, updateDoc, collection, getDocs, query, where, addDoc, writeBatch, arrayRemove, arrayUnion, getDoc } from 'firebase/firestore';
import { RankingService } from '../../services/rankingService';

interface Props {
  t: Tournament;
  allTeams: Team[];
  players: Player[];
  rankingConfig: RankingConfig;
  db: any;
  getCollectionPath: (col: string) => string;
  onClose: () => void;
}

export function AdminPodiumEditModal({ t, allTeams, players, rankingConfig, db, getCollectionPath, onClose }: Props) {
  const [championId, setChampionId] = useState(t.championId || '');
  const [runnerUpId, setRunnerUpId] = useState(t.runnerUpId || '');
  const [thirdPlaceId, setThirdPlaceId] = useState(t.thirdPlaceId || '');
  const [semifinalist1Id, setSf1Id] = useState(t.semifinalist1Id || '');
  const [semifinalist2Id, setSf2Id] = useState(t.semifinalist2Id || '');
  
  const [isSaving, setIsSaving] = useState(false);

  const tournamentTeamsIds = new Set<string>();
  (t.registeredTeamIds || []).forEach(id => tournamentTeamsIds.add(id));
  t.brackets?.forEach(b => b.matches.forEach(m => {
    if (m.team1Id && m.team1Id !== 'BYE') tournamentTeamsIds.add(m.team1Id);
    if (m.team2Id && m.team2Id !== 'BYE') tournamentTeamsIds.add(m.team2Id);
  }));
  t.zones?.forEach(z => z.teams.forEach(tm => tournamentTeamsIds.add(tm.teamId)));
  const tournamentTeams = allTeams.filter(tm => tournamentTeamsIds.has(tm.id));

  const getTeamName = (teamId: string) => {
    if (!teamId) return 'Ninguno';
    const team = allTeams.find(tm => tm.id === teamId);
    if (!team) return 'Equipo Desconocido';
    const p1 = players.find(p => p.id === team.player1Id);
    const p2 = players.find(p => p.id === team.player2Id);
    return `${p1?.name.split(' ').pop() || ''} / ${p2?.name.split(' ').pop() || ''}`;
  };

  const handleSave = async () => {
    const isConfirmed = await window.confirm("¿Estás seguro de modificar el podio? Esto recalculará los puntos del ranking de todos los jugadores involucrados.");
    if (!isConfirmed) {
      return;
    }

    setIsSaving(true);
    try {
      const batch = writeBatch(db);

      // 1. Rollback old points and achievements
      // We must find all players who were registered in this tournament
      const allPlayerIds = new Set<string>();
      tournamentTeams.forEach(team => {
        if (team.player1Id) allPlayerIds.add(team.player1Id);
        if (team.player2Id) allPlayerIds.add(team.player2Id);
      });

      for (const pId of Array.from(allPlayerIds)) {
        // Fetch points history to find entries related to this tournament
        const historyRef = collection(db, getCollectionPath('players'), pId, 'pointsHistory');
        const q = query(historyRef, where('type', '==', 'tournament'));
        const snap = await getDocs(q);
        
        for (const docSnap of snap.docs) {
          const data = docSnap.data();
          // Match by tournamentId or fallback to reason string matching
          if (data.tournamentId === t.id || (data.reason && data.reason.includes(t.name))) {
            batch.delete(docSnap.ref);
          }
        }

        // Remove old achievements for this tournament
        const playerRef = doc(db, getCollectionPath('players'), pId);
        const playerSnap = await getDoc(playerRef);
        if (playerSnap.exists()) {
          const playerData = playerSnap.data();
          if (playerData.achievements) {
            const achievementsToRemove = playerData.achievements.filter((a: any) => a.text && a.text.includes(t.name));
            if (achievementsToRemove.length > 0) {
              for (const a of achievementsToRemove) {
                batch.update(playerRef, {
                  achievements: arrayRemove(a)
                });
              }
            }
          }
        }
      }

      await batch.commit();

      // 2. Re-assign points and achievements based on the new podium
      const newPodium = [
        { teamId: championId, rank: 1 },
        { teamId: runnerUpId, rank: 2 },
        { teamId: thirdPlaceId, rank: 3 },
        { teamId: semifinalist1Id, rank: 4 },
        { teamId: semifinalist2Id, rank: 4 }
      ];

      for (const team of tournamentTeams) {
        const rankInfo = newPodium.find(r => r.teamId === team.id);
        const finalRank = rankInfo ? rankInfo.rank : 99;

        let pointsToAward = 0;
        if (finalRank === 1) pointsToAward = rankingConfig.champion;
        else if (finalRank === 2) pointsToAward = rankingConfig.runnerUp;
        else if (finalRank === 3) {
          pointsToAward = rankingConfig.awardPointsTo === 'third_place' 
            ? (rankingConfig.thirdPlace || 0)
            : rankingConfig.semifinalist;
        }
        else if (finalRank === 4) {
          pointsToAward = rankingConfig.awardPointsTo === 'third_place'
            ? 0
            : rankingConfig.semifinalist;
        }

        const achievementType: 'oro' | 'plata' | 'bronce' | null =
          finalRank === 1 ? 'oro' :
            finalRank === 2 ? 'plata' :
              finalRank === 3 ? 'bronce' : null;

        const achievement = achievementType ? {
          type: achievementType,
          text: `${finalRank}° Puesto - ${t.name} (${t.category || ''})`
        } : null;

        const playerIds = [team.player1Id, team.player2Id].filter(Boolean);
        for (const pId of playerIds) {
          const playerRef = doc(db, getCollectionPath('players'), pId);
          
          if (achievement) {
            await updateDoc(playerRef, {
              achievements: arrayUnion(achievement)
            });
          }

          const historyRef = collection(db, getCollectionPath('players'), pId, 'pointsHistory');
          await addDoc(historyRef, {
            points: pointsToAward,
            type: 'tournament',
            tournamentId: t.id,
            organizerId: t.organizerId || auth.currentUser?.uid || 'legacy_organizer',
            isGlobal: t.isMaster || t.category === 'MASTER' || t.name.toLowerCase().includes('master'),
            reason: finalRank <= 4 ? `Torneo: ${t.name} (${finalRank <= 2 ? (finalRank === 1 ? 'Campeón' : 'Finalista') : (finalRank === 3 ? '3er Puesto' : 'Semi')})` : `Participación: ${t.name}`,
            createdAt: Date.now()
          });

          // Re-calculate points
          await RankingService.recalculatePlayerPoints(pId);
        }
      }

      // 3. Update the tournament document
      await updateDoc(doc(db, getCollectionPath('tournaments'), t.id), {
        championId: championId || null,
        runnerUpId: runnerUpId || null,
        thirdPlaceId: thirdPlaceId || null,
        semifinalist1Id: semifinalist1Id || null,
        semifinalist2Id: semifinalist2Id || null
      });

      alert("Podio guardado y puntos recalculados exitosamente.");
      onClose();

    } catch (e) {
      console.error("Error updating podium:", e);
      alert("Ocurrió un error al guardar el podio.");
    } finally {
      setIsSaving(false);
    }
  };

  const TeamSelect = ({ label, value, onChange, iconColor }: any) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-black text-white/70 uppercase tracking-widest flex items-center gap-1.5">
        <Trophy size={12} className={iconColor} />
        {label}
      </label>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-black/40 border border-white/10 rounded-xl px-3 py-2.5 text-sm text-white font-bold outline-none focus:border-theme-primary transition-all"
      >
        <option value="">-- Ninguno --</option>
        {tournamentTeams.map(team => (
          <option key={team.id} value={team.id}>{getTeamName(team.id)}</option>
        ))}
      </select>
    </div>
  );

  return createPortal(
    <div className="fixed inset-0 z-[99999] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm animate-in fade-in duration-200">
      <div className="bg-slate-800 w-full max-w-md rounded-2xl border border-white/20 shadow-2xl p-5 sm:p-6 relative animate-in zoom-in-95 duration-200 max-h-[90vh] overflow-y-auto custom-scrollbar">
        <button
          onClick={onClose}
          disabled={isSaving}
          className="absolute top-4 right-4 text-white/40 hover:text-white transition-all bg-white/5 hover:bg-white/10 p-1.5 rounded-lg"
        >
          <X size={18} />
        </button>

        <h3 className="text-xl font-black text-white uppercase tracking-wider mb-2 pr-8">
          Editar Podio
        </h3>
        <p className="text-xs text-white/70 mb-6 flex gap-2 bg-black/20 p-3 rounded-xl border border-white/5">
          <AlertTriangle size={16} className="shrink-0 text-yellow-500 mt-0.5" />
          Al guardar, se recalcularán automáticamente los puntos y logros para todos los jugadores inscritos.
        </p>
        
        <div className="flex flex-col gap-4 mb-8">
          <TeamSelect 
            label="Campeón (1° Puesto)" 
            value={championId} 
            onChange={setChampionId} 
            iconColor="text-yellow-400"
          />
          <TeamSelect 
            label="Subcampeón (2° Puesto)" 
            value={runnerUpId} 
            onChange={setRunnerUpId} 
            iconColor="text-gray-300"
          />
          
          <div className="h-px w-full bg-white/10 my-1" />
          
          <TeamSelect 
            label="Tercer Puesto" 
            value={thirdPlaceId} 
            onChange={setThirdPlaceId} 
            iconColor="text-amber-500"
          />
          
          <div className="h-px w-full bg-white/10 my-1" />
          
          <TeamSelect 
            label="Semifinalista 1" 
            value={semifinalist1Id} 
            onChange={setSf1Id} 
            iconColor="text-blue-400"
          />
          <TeamSelect 
            label="Semifinalista 2" 
            value={semifinalist2Id} 
            onChange={setSf2Id} 
            iconColor="text-blue-400"
          />
        </div>

        <div className="flex flex-col gap-2">
          <button
            onClick={handleSave}
            disabled={isSaving}
            className="w-full py-3.5 rounded-xl bg-blue-600 text-white font-black uppercase tracking-widest transition-all hover:bg-blue-500 hover:scale-[1.02] active:scale-95 shadow-lg disabled:opacity-50 disabled:pointer-events-none"
          >
            {isSaving ? 'Guardando...' : 'Guardar y Recalcular'}
          </button>
          <button
            onClick={onClose}
            disabled={isSaving}
            className="w-full py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-bold transition-all text-sm uppercase tracking-wider disabled:opacity-50"
          >
            Cancelar
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}
